// 결과 화면용 인생 요약: 연표, 한 줄 결론, 돈의 흐름, 근거 목록. 진행 기록(run)만 읽고 어떤 값도 바꾸지 않는다.
import { getLifeTables, lifeReasons, lifeRecordText } from './lifeEvents.js';
import { opensFor, jobTitle } from './engine.js';
import { GROUP_LABEL } from './rules.js';
import { occupationOf, OCCUPATION_LABEL, incomeIndex, TODAY_YEAR, todaySeries } from './economy.js';
import { LUCK_TRANSITIONS } from './luckTransitions.js';

const OUTCOME = { success: '이뤄졌다', failure: '뜻대로 되지 않았다', skipped: '하지 않았다' };
const STATEFUL = ['marriage', 'birth', 'house', 'pet', 'divorce', 'remarriage'];

/** 만원 단위 금액을 읽기 쉬운 문장으로 (예: 12345 → 1억 2,345만원) */
export const manwonText = (v) => {
  const n = Math.round(v), abs = Math.abs(n), sign = n < 0 ? '-' : '';
  if (abs >= 10000) { const eok = Math.floor(abs / 10000), rest = abs % 10000; return `${sign}${eok}억${rest ? ` ${rest.toLocaleString()}만원` : '원'}`; }
  return `${sign}${abs.toLocaleString()}만원`;
};
const gradeBadge = (text) => (text ?? '').split('(')[0].trim();
const GRADE_KEY = { marriage: 'firstMarriage', birth: 'birth', house: 'housing', pet: 'pet', jobChange: 'jobChange', illness: 'health', divorce: 'divorce', remarriage: 'remarriage' };
export const lifeGrade = (type) => gradeBadge(getLifeTables().grade[GRADE_KEY[type]]);

/** 나이순 인생 연표: 대운 전환 · 인생 사건(이유와 내가 고른 것) · 직업 변화 */
export function buildLifeTimeline(run) {
  const rows = [];
  const reasonsFor = (age, type) => { try { return lifeReasons(run, age, type, opensFor(run, age).open); } catch { return []; } };
  for (const year of run.history ?? []) {
    const luck = year.luck;
    if (luck && luck.yearsSince === 0 && year.age > 0) {
      const label = luck.type ? LUCK_TRANSITIONS.labels?.[luck.type] ?? luck.type : '';
      rows.push({ age: year.age, kind: 'luck', order: 0, title: `대운이 바뀜 · ${GROUP_LABEL[luck.group] ?? ''} 기운${label ? ` · ${label}` : ''}` });
    }
  }
  for (const entry of run.eventHistory ?? []) {
    if (String(entry.eventId).startsWith('s_')) { // 새 형식 장면
      rows.push({ age: entry.age, kind: 'scene', order: 1, title: entry.title, minor: entry.outcome === 'skipped',
        chosen: `${entry.choiceLabel ?? '선택 기록 없음'}${OUTCOME[entry.outcome] ? ` · ${OUTCOME[entry.outcome]}` : ''}` });
      continue;
    }
    if (!String(entry.eventId).startsWith('life_')) continue;
    const type = entry.eventId.slice(5), changed = entry.choiceKind === 'act' && entry.outcome === 'success';
    const chosen = `${entry.choiceLabel ?? '선택 기록 없음'}${OUTCOME[entry.outcome] ? ` · ${OUTCOME[entry.outcome]}` : ''}`;
    rows.push({ age: entry.age, kind: 'life', order: 1, type, grade: lifeGrade(type),
      title: changed && STATEFUL.includes(type) ? lifeRecordText(type) : entry.title, why: reasonsFor(entry.age, type), chosen,
      minor: entry.outcome === 'skipped' }); // 거절한 제안은 연표에서 흐리게 보여 준다
  }
  for (const entry of run.log ?? []) {
    if (entry.kind === 'lifeEvent' && entry.auto) {
      rows.push({ age: entry.age, kind: 'life', order: 1, type: entry.lifeType, grade: lifeGrade(entry.lifeType), title: entry.text,
        why: reasonsFor(entry.age, entry.lifeType), chosen: '선택 없이 일어남', auto: true });
    } else if (entry.kind === 'job') rows.push({ age: entry.age, kind: 'job', order: 2, title: entry.text });
  }
  return rows.sort((a, b) => a.age - b.age || a.order - b.order);
}

/** 맨 위 한 줄 결론. 인생 사건을 켠 진행이 아니면 나이와 직업만 담는다. */
export function lifeOneLiner(run) {
  const life = run.life, endAge = Math.max(0, (run.age ?? 1) - 1), parts = [`${endAge}세`];
  if (run.job) parts.push(`${OCCUPATION_LABEL[occupationOf(run.job)] ?? ''} · ${jobTitle(run.job)}`.replace(/^ · /, ''));
  if (life) {
    const m = life.marriage?.status;
    parts.push(m === 'married' ? '결혼 중' : m === 'divorced' ? '이혼' : '미혼');
    parts.push(`자녀 ${life.children?.length ?? 0}명`);
    if (life.economy?.series?.length) parts.push(`마지막 순자산 ${manwonText(todaySeries(run).at(-1).netWorth)}`);
  }
  return parts.join(' · ');
}

// 같은 나이대 실제 가구 평균(가구주 연령 구간의 가운데 나이에 놓은 값)
const ANCHOR_AGE = { '29세이하': 27, '30-39세': 35, '40-49세': 45, '50-59세': 55, '60세이상': 63 };

/** 돈의 흐름: 본인 기록, 같은 나이대 통계 평균, 사건 표시. 경제 기록이 없으면(옛 저장) null */
export function buildMoneyFlow(run, timeline = buildLifeTimeline(run)) {
  const series = todaySeries(run);
  if (!series.length) return null;
  const anchor = getLifeTables().economy.netWorthAnchor;
  // 실제 통계(2025년 가격)는 같은 기준(2026년 돈 가치)으로 바꿔서 겹쳐 본다.
  const anchors = Object.entries(ANCHOR_AGE).map(([key, age]) => ({ age, value: anchor[key] * incomeIndex(TODAY_YEAR) })).filter((p) => Number.isFinite(p.value));
  const peak = series.reduce((best, p) => (p.netWorth > best.netWorth ? p : best), series[0]);
  const low = series.reduce((worst, p) => (p.netWorth < worst.netWorth ? p : worst), series[0]);
  const at60 = series.find((p) => p.age === 60) ?? null, last = series[series.length - 1];
  const marks = timeline.filter((r) => ['house', 'illness', 'divorce', 'marriage', 'birth'].includes(r.type) && series.some((p) => p.age === r.age));
  return { series, anchors, peak, low, at60, last, marks };
}

/** 근거와 한계 목록(근거 등급은 표 데이터에서 가져온다) */
export function lifeBasis() {
  const g = getLifeTables().grade;
  return [
    ['결혼(초혼)', g.firstMarriage, '혼인·인구 통계(KOSIS 2022~2025). 현재 행태 기준이라 옛 세대보다 결혼이 늦고 적다.'],
    ['결혼 비용', '일부 통계(민간 설문)', '신혼집을 뺀 결혼 준비 비용 평균 6,298만원(가연 설문, 2024, 결혼 5년 차 이하 1,000명)에서 축의금 1,350만원(평균 축의금 9만원 카카오페이 2024 × 하객 150명 가정)을 뺀 순비용을 쓴다. 공식 통계가 아니다.'],
    ['이혼·재혼 비용', '게임 추정', '이혼 약 600만원(변호사 착수금 400~700만원의 가운데값, 법률 안내글), 재혼은 결혼 비용의 절반에서 축의금 400만원을 뺀 값. 재산은 절반으로 나뉜다.'],
    ['출산', g.birth, '연령별 출산율(KOSIS)을 결혼한 사람 기준으로 환산해 맞췄다.'],
    ['주택 구입', g.housing, '연령대별 매입 건수(한국부동산원 R-ONE). 첫 주택이 아니라 모든 매입이다.'],
    ['반려동물', g.pet, '반려동물 양육현황조사 2025(1회 조사).'],
    ['직업 전환', g.jobChange, '구직급여 신청자 수로 근사했다. 해고만이 아니라 직업이 바뀔 계기의 빈도다.'],
    ['병원비', g.health, '건강보험 진료비 분포. 병명은 다루지 않고 수명에도 영향을 주지 않는다.'],
    ['이혼·재혼', g.divorce, '통계 없이 사주(배우자 자리와 운)로 정했다. 사별은 다루지 않는다.'],
    ['물가', '통계 기반(과거) · 게임 추정(미래)', '진행 중에는 모든 금액이 그해 수준이고, 결과 화면은 2026년 돈 가치로 바꿔 보여 준다(같은 소득지수로 환산). 소득·집값·생활비·병원비·결혼 비용 모두 1인당 국민총소득(명목, 세계은행, 1960~2025)의 변화를 따랐다. 소비자물가만으로 과거 임금을 줄이면 실제 임금 상승이 빠져 과거 사람이 집을 살 수 없게 되어서다. 2026년부터는 물가 연 2%(한국은행 물가안정목표)와 실질 성장 1.5%를 합친 연 3.5%를 가정했다.'],
    ['소득', '통계 기반(2025)', '직종 9개 × 경력 × 성별 월임금총액(고용노동부, KOSIS). 자영업자·군인은 가까운 직종으로 계산했다.'],
    ['자산', '일부 통계(모델)', '가계금융복지조사 2025의 소비·세금·이자 값으로 만든 모델이다. 상속·증여·주식·사업 자산은 없다.'],
  ].map(([name, grade, note]) => ({ name, grade: gradeBadge(grade), note }));
}
